import _ from 'lodash'
import { DateTime } from 'luxon'
import map from 'lodash/map'
import fill from 'lodash/fill'
import flatten from 'lodash/flatten'
import React, { useState } from 'react'
import { FormikProps, useFormik } from 'formik';
import { FormProps, FormDrawerProps } from '~/component/form/FormProps';
import { useFormikContext } from 'formik';
import { ButtonLoader } from '~/component/common/Loader';

import * as validation from '~/utils/validation';

import * as api from '~/myApi'
import client from '~/utils/client'
import { getSingleByteCharactersNumberOfCharacters } from '~/utils/codePoint'
import * as enums from '~/const/enums'

import { Drawer, Modal } from '~/component/common/Dialog';
import ComfirmMessage from '~/component/common/ComfirmMessage'

import FormGroup from '~/component/form/FormGroup'

import FormField from '~/component/form/FormField'
import TextField from '~/component/form/TextField'
import Switch from '~/component/form/Switch'
import RadioButtons from '~/component/form/RadioButtons'
import TargetRatios from '~/component/form/TargetRatios'

import DateRange from '~/component/form/DateRange'
import TargetRetioTable from '~/component/survey/TargetRetioTable'
import Cluster from '~/component/survey/Cluster'
import SurveyReset from '~/component/survey/SurveyReset'


import Questions from '~/component/surveyForm/Questions'
import { Button } from '@mui/material';


import createMessages from '~/const/message'

type FormType = api.ServerUpdateSurveyRequest & api.TypesSurveyFullInfo;

export const SurveyForm: React.FC<FormProps<FormType>> = (props) => {
  const { setFieldValue } = useFormikContext<FormType>()

  const isPre = props.values?.type === api.EnumsSurveyType.SURVEY_TYPE_PRE_AD

  return (
    <>
      {
        props.values?.id && (
          <FormField label="サーベイID" >
            {props.values?.id}
          </FormField>
        )
      }

      <FormField label="サーベイ名" errorMessage={props.errors?.name} required>
        <TextField {...{
          name: 'name',
          type: 'string',
          value: props.values?.name,
          error: !!props.errors?.name
        }} />
      </FormField>

      <FormField label="配信">
        <Switch {...{
          name: 'status',
          value: props.values?.status,
          trueValue: api.EnumsSurveyStatus.SURVEY_STATUS_ON,
          falseValue: api.EnumsSurveyStatus.SURVEY_STATUS_OFF,
        }} />
      </FormField>

      <FormField label="サーベイ配信期間" errorMessage={props.errors?.surveyStartAt} required>
        <DateRange {...{
          startAtName: 'surveyStartAt',
          endAtName: 'surveyEndAt',
          startAt: props.values?.surveyStartAt,
          endAt: props.values?.surveyEndAt
        }} />
      </FormField>

      <FormField label="目標回答数" errorMessage={props.errors?.targetCollectionNum} required>
        <TextField {...{
          name: 'targetCollectionNum',
          type: 'number',
          value: props.values?.targetCollectionNum,
          error: !!props.errors?.targetCollectionNum
        }} />
      </FormField>

      <FormField label="回収ペースの指定">
        <Switch {...{
          name: 'reset.isActive',
          value: props.values?.reset?.isActive,
          onChange: (v: boolean) => {
            setFieldValue('reset.days', v ? 1 : undefined)
            setFieldValue('reset.periodTargetCollectionNum', v ? 1000 : undefined)
          }
        }} />
      </FormField>

      {props.values?.reset?.isActive && (
        <FormField label="回収ペース詳細" errorMessage={props.errors?.reset}>
          <SurveyReset {...{
            days: props.values?.reset?.days,
            periodTargetCollectionNum: props.values?.reset?.periodTargetCollectionNum,
            error: !!props.errors?.reset
          }} />
        </FormField>
      )}

      <FormField label="調査カテゴリ">
        <RadioButtons {...{
          name: 'type',
          value: props.values?.type,
          items: enums.SURVEY_TYPE,
          onChange: (value) => {
            if (value === api.EnumsSurveyType.SURVEY_TYPE_AD_CONTACT) {
              setFieldValue('recency', {
                isActive: true,
                days: 3
              })
              setFieldValue('targetRatio', {
                contactRatio: {
                  isActive: true,
                  adContactStatusTrueRatio: 1,
                  adContactStatusFalseRatio: 1
                }
              })
            } else {
              setFieldValue('recency', {})
              setFieldValue('targetRatio', {
                contactRatio: {}
              })
            }
          }
        }} />
      </FormField>
      {!isPre &&
        <FormField label="接触期間" name="recency" isToggleContents
          errorMessage={props.errors?.recency}
          toggleOnChange={(v) => setFieldValue('recency.days', v ? 1 : undefined)}
          required={props.values?.recency?.isActive}
        >
          {props.values?.recency?.isActive && (
            <TextField {...{
              name: 'recency.days',
              type: 'number',
              value: props.values?.recency?.days,
              unit: "日以内",
              error: !!props.errors?.recency
            }} />
          )}
        </FormField>
      }

      <FormField label="メモ">
        <TextField {...{
          multiline: true,
          rows: 3,
          name: 'memo',
          type: 'string',
          value: props.values?.memo
        }} />
      </FormField>

      <FormGroup title="配信先・割付比率"
        warningMessage={!props.isCreate ? '※ 配信後に割付属性の追加/変更を行う場合、取得済みの回答ログがリセットされます' : undefined}
      >
        {!isPre && (
          <FormField label="接触・非接触" name="targetRatio.contactRatio" isToggleContents isTargeting>
            {props.values?.targetRatio?.contactRatio?.isActive && (
              <TargetRatios {...{
                name: 'targetRatio.contactRatio',
                value: props.values?.targetRatio?.contactRatio,
                items: enums.AD_CONTACT_STATUS_RATIO,
                targetCollectionNum: props.values?.targetCollectionNum
              }} />
            )}
          </FormField>
        )}
        <FormField label="クラスタ" name="targetRatio.clusterRatio" isToggleContents isTargeting toggleOnChange={(v) => setFieldValue('targetRatio.clusterType', undefined)}>
          {props.values?.targetRatio?.clusterRatio?.isActive && (<>
            <Cluster {...{
              value: props.values?.targetRatio,
              targetCollectionNum: props.values?.targetCollectionNum
            }} />
          </>)}
        </FormField>
        <FormField label="キャリア" name="targetRatio.carrerRatio" isToggleContents isTargeting>
          {props.values?.targetRatio?.carrerRatio?.isActive && (
            <TargetRatios {...{
              name: 'targetRatio.carrerRatio',
              value: props.values?.targetRatio?.carrerRatio,
              items: enums.CARRER_RATIO,
              targetCollectionNum: props.values?.targetCollectionNum
            }} />
          )}
        </FormField>
        <FormField label="デバイス" name="targetRatio.deviceRatio" isToggleContents isTargeting>
          {props.values?.targetRatio?.deviceRatio?.isActive && (
            <TargetRatios {...{
              name: 'targetRatio.deviceRatio',
              value: props.values?.targetRatio?.deviceRatio,
              items: enums.DEVICE_RATIO,
              targetCollectionNum: props.values?.targetCollectionNum
            }} />
          )}
        </FormField>
        <FormField label="OS" name="targetRatio.osRatio" isToggleContents isTargeting>
          {props.values?.targetRatio?.osRatio?.isActive && (
            <TargetRatios {...{
              name: 'targetRatio.osRatio',
              value: props.values?.targetRatio?.osRatio,
              items: enums.OS_RATIO,
              targetCollectionNum: props.values?.targetCollectionNum
            }} />
          )}
        </FormField>

        {
          props.values?.targetRatio && (
            <TargetRetioTable {...{
              value: props.values?.targetRatio,
              targetCollectionNum: props.values?.targetCollectionNum,
              progress: props.values.progress,
              hideContactRatio: !isPre,
            }} />
          )
        }
      </FormGroup>

      <FormGroup title="設問"
        warningMessage={!props.isCreate ? '※ 配信後に設問・回答の追加/変更を行う場合、取得済みの回答ログがリセットされます' : undefined}
      >
        <Questions value={props.values?.questions} error={props.errors?.questions} />
      </FormGroup>
    </>
  )
}

type SurveyDrawerProps = React.FC<FormDrawerProps<FormType> & {
  openDeleteConfirmModal?: () => void;
  changeCopyMode?: () => void;
  isCopyMode: boolean;
  isConfirm?: boolean;
}>

export const SurveyDrawer: SurveyDrawerProps = (props) => {
  const {
    isOpen,
    onClose,
    initialValues,
    isContentsLoading
  } = props
  const surveyMessage = createMessages()

  const isCreate = !('id' in initialValues) || props.isCopyMode;

  const generateProgressTotalCollectedNum = (progress: api.TypesProgress[] | undefined) => {
    return _.size(progress) && _(_.map(progress, (p) => p.collectedNum || 0)).reduce(_.add)
  }

  const onSubmit = (data: FormType) => {
    const progressTotalCollectedNum = generateProgressTotalCollectedNum(data.progress)
    const isConfirm = !isCreate && progressTotalCollectedNum !== 0

    if (isConfirm) {
      setIsConfirm(true)
    } else {
      return postSurveyData(data)
    }
  }

  const postSurveyData = (data: FormType) => {
    //本当は中で入れたほうがいいけどちょっと苦労したのでここでorder入れる

    const params = Object.assign({}, data, {
      questions: data?.questions?.map((q, i) => {
        return Object.assign({}, q, { order: i })
      })
    })

    if (isCreate) {
      return client.surveyAdminApiCreateSurvey(params, {}).then((d) => {
        surveyMessage.createSuccess(params?.name)
        onClose(true)
      }).catch(({ error }) => {
        surveyMessage.error(error.message)
      })
    } else {
      return client.surveyAdminApiUpdateSurvey(params, {}).then((d) => {
        surveyMessage.updateSuccess(params?.name)
        onClose(true)
        setIsConfirm(false)
      }).catch((error) => {
        surveyMessage.error(error)
      })
    }
  }

  const questions = initialValues.questions && _.map(initialValues.questions, (q) => {
    return Object.assign({}, q)
  })

  const now = DateTime.local();
  const surveyStartAt = now.startOf('day').toSeconds().toString()
  const surveyEndAt = Math.floor(now.endOf('day').plus({ 'day': 6 }).toSeconds()).toString()

  let init = _.merge({}, {
    name: '',
    status: api.EnumsSurveyStatus.SURVEY_STATUS_ON,
    surveyStartAt: surveyStartAt,
    surveyEndAt: surveyEndAt,
    recency: {},
    reset: {
      isActive: false
    },
    questions: [{
      text: '',
      kind: '',
      random: false,
      answers: flatten(map(fill<api.TypesAnswer>(Array(5), {
        text: '',
        fixOrder: false,
        exclusive: false,
        lift: '0'
      }), (a, j) => Object.assign({}, a, { order: j })))
    }],
    targetRatio: {
      clusterRatio: {},
      contactRatio: {},
      deviceRatio: {},
      carrerRatio: {},
      osRatio: {},
    }
  }, initialValues)

  if (props.isCopyMode && initialValues.id) {
    init = _.omit(init, 'id')
  }

  const formik: FormikProps<FormType> = useFormik<FormType>({
    validate: validation.createValidationSchema(VALIDATION_SCHEME),
    initialValues: isCreate && !props.isCopyMode ? init : Object.assign({}, init, { questions: questions }),
    onSubmit,
    enableReinitialize: true,
    validateOnChange: false,
  })
  const headerTitle = `${props.title}${isCreate ? props.isCopyMode ? 'のコピー' : '作成' : '編集'}`

  const [isConfirmModal, setIsConfirm] = useState<boolean>(false)
  const [isReportDownloading, setIsReportDownloading] = useState<boolean>(false)

  const downloadReport = () => {
    if (isCreate) return
    const errorMessage = 'ダウンロードに失敗しました。'
    setIsReportDownloading(true)
    client.surveyAdminApiGetReport(initialValues.id!).then(({ data }) => {
      fetch(data.url!, {
        method: 'GET',
        headers: {
          'Content-Type': 'text/csv'
        },
      }).then(async (data) => {
        const blob = await data.blob()

        const downloadTag = document.createElement('a')
        downloadTag.href = URL.createObjectURL(blob)

        const format = 'yyyy.MM.dd_HH_mm_ss'

        const fileName = `${initialValues.id}_${initialValues.name}_answer_raw_${DateTime.now().toFormat(format)}.csv`

        downloadTag.download = fileName
        downloadTag.click()

        setIsReportDownloading(false)
        surveyMessage.success(`「${fileName}」のダウンロードが完了しました。`)
      }).catch((e) => {
        surveyMessage.error(errorMessage)
      })
    }).catch((e) => {
      surveyMessage.error(errorMessage)
    })
  }

  return (
    <Drawer {...{
      title: headerTitle, formik, isOpen, onClose, isContentsLoading, buttons: (
        <>
          {!!(!isCreate && generateProgressTotalCollectedNum(initialValues.progress)) && <Button onClick={downloadReport} disabled={isReportDownloading}>
            {isReportDownloading && <ButtonLoader size={16} />}
            レポートダウンロード
          </Button>}
          {!isCreate && <Button onClick={props.changeCopyMode}>コピーして新規作成</Button>}
          {!isCreate && <Button onClick={props.openDeleteConfirmModal} color="error">削除</Button>}
        </>
      )
    }}>
      <SurveyForm values={formik.values} errors={formik.errors} isCreate={isCreate} />
      <Modal
        isOpen={isConfirmModal}
        onClose={() => {
          setIsConfirm(false)
          formik?.resetForm();
        }}
        onSubmit={() => postSurveyData(formik.values)}
        title={`編集確認`}
        submitButtonText="保存"
      >
        <ComfirmMessage {...{ message: '配信後に割付属性・設問・回答の追加/変更を行う場合、取得済みの回答ログがリセットされます' }} />
      </Modal>
    </Drawer>
  )
};

export default SurveyForm

export const VALIDATION_SCHEME: validation.ValidationSchemaType<FormType> = {
  name: validation.check((value: FormType['name']) => { return value === '' ? '必須' : '' }),
  targetCollectionNum: validation.check((value: FormType['targetCollectionNum']) => { return !value ? '必須' : '' }),
  recency: validation.check((value: FormType['recency']) => { return value?.isActive && !value?.days ? '必須' : '' }),
  surveyStartAt: validation.check((value: FormType['surveyStartAt'], values) => {
    const startAt = typeof value === 'string' ? parseInt(value) : 0
    const endAt = typeof values?.surveyEndAt === 'string' ? parseInt(values?.surveyEndAt) : 0

    if(!startAt && !endAt) return '必須'

    return startAt && startAt >= endAt ? '開始日は終了日時より前の日付を入れてください' : ''
  }),
  surveyEndAt: validation.check((value: FormType['surveyStartAt'], values) => {
    const endAt = typeof value === 'string' ? parseInt(value) : 0
    const startAt = typeof values?.surveyStartAt === 'string' ? parseInt(values?.surveyStartAt) : 0
    return endAt && startAt >= endAt ? '終了日は開始日時より後の日付を入れてください' : ''
  }),
  questions: validation.check(
    (value: FormType['questions']) => _.chain(value)
      .map((v, i) => {
        if (!v.text) {
          return `【設問${i + 1}】テキストは必須です`
        }
        const QUESTION_TEXT_MAX_NUM = 50
        if (getSingleByteCharactersNumberOfCharacters(v.text) / 2 > QUESTION_TEXT_MAX_NUM) return `【設問${i + 1}】テキストは全角${QUESTION_TEXT_MAX_NUM}文字以下で設定してください`
        const QUESTION_KIND_MAX_NUM = 20
        if (v.kind!.length > QUESTION_KIND_MAX_NUM) return `【設問${i + 1}】設問種別は${QUESTION_KIND_MAX_NUM}文字以下で設定してください`

        const ANSWER_TEXT_MAX_NUM = 15
        return _.map(v.answers, (answer, j) => {
          const answerValidText = `【設問${i + 1}】答え${j + 1}`
          if (!answer.text) return `${answerValidText}は必須です`
          console.log(getSingleByteCharactersNumberOfCharacters(answer.text))
          if (getSingleByteCharactersNumberOfCharacters(answer.text) / 2 > ANSWER_TEXT_MAX_NUM) return `${answerValidText}は全角${ANSWER_TEXT_MAX_NUM}文字以下で設定してください`
        })
      })
      .flattenDeep()
      .value()
      .find(e => e)
  ),
};
